import { CompactUserResponse, UserId } from '@business/entities/account';
import { ProjectResponse } from '@business/entities/project';
import { InvoiceDiscountData } from '@modules/common/InvoiceDiscount';
import { OrgAddressResponse, OrgResponse } from '../organization';
import { SubscriptionInterval } from '../project/SubscriptionResponse';
import { InvoiceSalesTaxData } from './../../../modules/common/InvoiceSalesTax/InvoiceSalesTax';
import { CompactOrderResponse } from './../order/OrderResponse';
import { OrderTermResponse } from './../order/OrderTermResponse';
import { ACHMethodRequest } from './../organization/PaymentResponse';
import { LineItem } from './../project/SubscriptionResponse';
import { ProposalDiscountFormData } from '@modules/proposal/ProposalBuilderPage/Components/ProposalProductsOverlay';
import { ContactResponse } from '../contact';

export type InvoiceId = string;
export enum InvoiceTypeEnum {
  project = 'project',
  subscription = 'subscription',
  oneTime = 'one-time',
  order = 'order',
  refund = 'refund',
  creditMemo = 'credit-memo',
}

export enum InvoiceDiscountType {
  percentage = 'percentage',
  fixed = 'fixed',
}
export const InvoiceDiscountLabel: Record<InvoiceDiscountType, string> = {
  percentage: 'Discount percent',
  fixed: 'Discount value',
};

export interface InvoiceFilesResponse {
  isEmail: boolean;
  url: string;
  name: string;
}

export interface InvoiceMulticurrencyResponse {
  balanceDefaut: string;
  totalAmountDefault: number;
  currency: string;
}

export interface CompactInvoiceResponse extends InvoiceMulticurrencyResponse {
  id: InvoiceId;
  type: InvoiceTypeEnum;
  totalAmount: number;
  amount: number;
  remaining: number;
  balance: string;
  totalTax: string;
  docNumber: string;
  billEmail: string;
  billingAddress: string;
  statementMemo: string;
  messageDisplayedInvoice: string;
  customerId: string;

  dueDate: number;
  invoiceDate: number;
  createdAt: number;
  settings: InvoiceSettingsResponse;

  items: LineItem[];
  discount: InvoiceDiscountData;
  salesTax: InvoiceSalesTaxData;
  method: string;
  status: SalesStatusType;
  isTemplate: boolean;
}

export enum SalesStatusType {
  paid = 'paid',
  closed = 'closed',
  partiallyPaid = 'partially paid',
  overdue = 'overdue',
  refunded = 'refunded',
  credited = 'credited',
  voided = 'voided',
  failed = 'failed',
  open = 'open',
  sent = "sent",
}

export enum SalesInvoiceType {
  invoice = 'invoice',
  salesReceipt = 'salesReceipt',
  payment = 'payment',
  refund = 'refund',
  creditMemo = 'credit-memo',
}

export interface TransactionInvoiceResponse extends InvoiceMulticurrencyResponse {
  id: InvoiceId;
  invoiceId: string;
  title: string;
  type: InvoiceTypeEnum;
  method: string;
  cycle: SubscriptionInterval;
  totalAmount: number;
  linesPrice: number;
  amount: number;
  remaining: number;
  balance: string;
  totalTax: string;
  docNumber: string;
  importId: string;
  billEmail: string;
  //   billingAddress: string;
  billingAddress: OrgAddressResponse;
  shippingAddress: OrgAddressResponse;
  statementMemo: string;
  messageDisplayedInvoice: string;
  customerId: string;
  project: ProjectResponse;
  user: CompactUserResponse;
  dueDate: number;
  invoiceDate: number;
  createdAt: number;
  settings: InvoiceSettingsResponse;
  pdfLink: string;
  downloadLink: string;

  paymentType: string;
  paymentLastFour: string;
  lastAmount: number;
  items: LineItem[];
  discount: InvoiceDiscountData | ProposalDiscountFormData;
  salesTax: InvoiceSalesTaxData;
  invoiceStatus: 'draft' | 'sent' | 'paid' | 'canceled';
  status: SalesStatusType;
  invoiceType: SalesInvoiceType;
  objectId: string;

  customerPoNo: string;
  shippedDate: number | null;
  shippingMethod: string;
  trackId: string;
  termId: string;
  salesReps: UserId[];
  salesRepsNames: string[];
  term?: OrderTermResponse;
  isTemplate: boolean;

  nmiToken: string;
  processor: string;
  /** Pay page only (GET payment/{invoice}): false when the company has no payment integration to charge a card. */
  onlinePayment?: boolean;

  url: string;

  paidVia?: string;
  organization?: OrgResponse;
  contact?: ContactResponse;

  // initialInvPercent?: number;
  sentAt?: number;
  initialPercent?: number;
}

export interface DetailedTransactionInvoice extends TransactionInvoiceResponse {
  order: CompactOrderResponse;
}

export interface InvoiceSettingsResponse {
  name: string;
  logo: string;
  address: string;
  city: string;
  state: string;
  postal: string;
  country: string;
  phone: string;
  terms: string;
  fromName: string;
  fromEmail: string;
}

export interface InvoiceAchRequest extends Omit<ACHMethodRequest, 'phone'> {
  save: boolean;
  amount: number;
}

export type InvoiceCardRequest = {
  name: string;
  cardNumber: string;
  expiryMonth: number | string;
  expiryYear: number | string;
  cvv: string;
  save: boolean;
  amount: number;
};
